import type { BundleItemDetailsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import type { CrystallizeProductData } from '@/ui/components/pages/ProductDetail/types/CrystallizeProductData';
import { DocShapeId } from '@/utils/shapeIds';
import type { ClassicPdpProductData } from './classic-pdp-content';

// A bundle is a catalogue document, so the bundle fragment narrows to its
// document member. Folders and products never carry the bundle components.
export type ClassicPdpBundleData = Extract<BundleItemDetailsFragment, { __typename: 'Document' }>;

/** Everything a classic tile can link: a hardware product or a bundle. */
export type ClassicPdpEntryData = ClassicPdpProductData | ClassicPdpBundleData;

/** One item the server resolved for a classic tile, keyed by its item path. */
export interface ClassicPdpEntry {
  path: string;
  data: ClassicPdpEntryData;
}

// Every payload the guards meet: page products, resolved entries and the raw
// bundle query result, which can also be a folder or a product.
type ClassicPdpGuardInput =
  | CrystallizeProductData
  | ClassicPdpEntryData
  | BundleItemDetailsFragment
  | null
  | undefined;

/**
 * Narrows a payload to the product shapes the classic section supports. It
 * renders a hardware purchase flow, and subscriptions or digital products have
 * no colors, cylinders or quantity to select.
 */
export function isClassicPdpProductData(data: ClassicPdpGuardInput): data is ClassicPdpProductData {
  if (!data || !('shape' in data)) {
    return false;
  }

  const shapeId = data.shape.identifier;

  return shapeId === DocShapeId.smartLock || shapeId === DocShapeId.accessory;
}

/** Narrows a payload to a bundle document, which carries its product slots. */
export function isClassicPdpBundleData(data: ClassicPdpGuardInput): data is ClassicPdpBundleData {
  return data?.__typename === 'Document' && 'bundleProducts' in data;
}

/**
 * Reads the resolved classic tile entries off a page payload. The payload is a
 * union of every shape result and reaches the renderers untyped, so the read
 * stays here instead of widening every prop type between the page and the tile.
 */
export function readClassicPdpProducts(pageContent: unknown): ClassicPdpEntry[] {
  const carrier = pageContent as { classicPdpProducts?: ClassicPdpEntry[] | null } | null;

  return carrier?.classicPdpProducts ?? [];
}
